import React, { useMemo } from 'react';
import Image from 'next/image';
import styled from '@benzinga/themetron';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { StoryCategory } from '@benzinga/advanced-news-manager';
import { PostDateType, PostElapsed } from './PostElapsed';
import { NodeCategory } from '@benzinga/basic-news-manager';
import { decode } from 'html-entities';
import { formatImageUrl } from '@benzinga/utils';
import { ProBadge, EdgeBadge } from '@benzinga/core-ui';

const PLACEHOLDER = '/next-assets/images/benzinga-post-card-placeholder.svg';
const BZ_PRO_PLACEHOLDER = '/next-assets/images/benzinga-pro-post-card-placeholder-light.svg';

export type PostCardLayout = 'default' | 'hero' | 'feed' | 'card';
export type PostCardSize = 'default' | 'compact' | 'nano';

export interface PostCardProps {
  author?: string | null | undefined;
  created?: string;
  dataAction?: string;
  dateType?: PostDateType;
  description?: string | null;
  hasEdgeBadge?: boolean;
  hideEmptyThumb?: boolean;
  hideImages?: boolean;
  image?: string | { url: string; alt?: string };
  showProBadge?: boolean;
  isOnlyProPost?: boolean;
  layout?: PostCardLayout;
  size?: PostCardSize;
  sizes?: string;
  sponsored?: boolean;
  sponsoredLabel?: string | null;
  title?: string | null;
  hidePublishDate?: boolean;
  loading?: 'lazy' | 'eager';
  stocks?: StoryCategory[] | NodeCategory[] | null;
  summarySlot?: React.ReactNode;
  tickerSlot?: React.ReactNode;
  id?: number;
  url?: string;
  imageWidth?: number;
  imagePreload?: boolean;
  fetchPriority?: 'auto' | 'low' | 'high';
  showCommentsIcon?: boolean;
  styles?: {
    titleLineHeight: 'normal' | 'snug' | 'tight' | 'loose';
  };
}

export const PostCard: React.FC<PostCardProps> = React.memo(
  ({
    created,
    dataAction,
    dateType = 'relative',
    description,
    fetchPriority,
    hasEdgeBadge,
    hideEmptyThumb = false,
    hidePublishDate = false,
    image,
    imagePreload,
    isOnlyProPost = false,
    layout = 'default',
    loading,
    showProBadge,
    size = 'default',
    sizes: sizesProp,
    sponsored,
    sponsoredLabel = null,
    styles,
    summarySlot,
    tickerSlot,
    title,
    url,
  }) => {
    const sizes =
      sizesProp ?? (layout === 'hero' ? '(max-width: 1023px) 100vw, 720px' : '(max-width: 800px) 160px, 200px');

    const postCardTitleClassname = `post-card-title ${styles?.titleLineHeight ? `leading-${styles?.titleLineHeight}` : `leading-${layout === 'hero' ? 'tight' : 'normal'}`} text-${
      size === 'compact' ? 'base' : layout === 'hero' && Number(title?.length) < 60 ? '4xl' : '2xl'
    }`;

    const imageSrc = image && (typeof image === 'object' ? image.url : formatImageUrl({ image }, true, true));

    const showProBadgeForDisplay = showProBadge ?? isOnlyProPost;
    const hasBadges = showProBadgeForDisplay || hasEdgeBadge;

    const sanitizedDescription = useMemo(() => {
      return { __html: description ? sanitizeHTML(description) : '' };
    }, [description]);

    const finalUrl = isOnlyProPost ? '#' : url;

    return (
      <PostCardWrapper $layout={layout} className="post-card-wrapper">
        <PostCardContainer
          className={`post-card post-card-${layout} post-card-size-${size}`}
          layout={layout}
          size={size}
          title={title ? decode(title) : undefined}
        >
          {(image || !hideEmptyThumb) && (
            <a
              className="post-card-article-link post-card-image image-wrapper"
              data-action={dataAction}
              href={finalUrl}
            >
              <Image
                alt={(typeof image === 'object' && image.alt) || title || ''}
                className="post-thumb"
                fill
                fetchPriority={fetchPriority}
                loading={imagePreload ? undefined : loading}
                priority={imagePreload}
                sizes={sizes}
                src={imageSrc || (isOnlyProPost ? BZ_PRO_PLACEHOLDER : PLACEHOLDER)}
                style={{ objectFit: 'cover' }}
              />
            </a>
          )}

          <div className="post-card-text lg:w-auto flex flex-col space-y-2">
            <div className="post-card-text-wrapper">
              {tickerSlot}
              <a className="post-card-article-link" data-action={dataAction} href={finalUrl}>
                {size === 'nano' ? (
                  <PostTitle className="post-title">
                    <ThreeLines className={`line-wrapper three-line ${postCardTitleClassname}`}>
                      {title && (
                        <>
                          {hasBadges && (
                            <span className="inline-block align-middle mr-2 flex-shrink-0">
                              {showProBadgeForDisplay ? <ProBadge /> : EdgeBadge}
                            </span>
                          )}
                          <span className="inline" dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
                        </>
                      )}
                    </ThreeLines>
                  </PostTitle>
                ) : (
                  <PostTitle className="post-title">
                    <DynamicLines className={`line-wrapper ${postCardTitleClassname}`}>
                      {title && (
                        <>
                          {hasBadges && (
                            <span className="inline-block align-middle mr-2 flex-shrink-0">
                              {showProBadgeForDisplay ? <ProBadge /> : EdgeBadge}
                            </span>
                          )}
                          <span className="inline" dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
                        </>
                      )}
                    </DynamicLines>
                  </PostTitle>
                )}
                {description && (layout === 'feed' || layout === 'card') && (
                  <div
                    className={`post-card-description lg:${
                      size === 'compact' || size === 'nano' ? 'text-xs' : 'text-lg'
                    }`}
                  >
                    <span className="post-teaser" dangerouslySetInnerHTML={sanitizedDescription} />
                  </div>
                )}
              </a>
              {summarySlot}
              <div className="post-card-article-link flex items-center">
                {!sponsored && layout !== 'hero' && created && !hidePublishDate ? (
                  <PostElapsed
                    className="font-normal text-gray-500 post-created-at"
                    created={created}
                    dateType={dateType}
                  />
                ) : null}
              </div>
              {sponsored ? <span className="news-item-sponsored-tag">{sponsoredLabel}</span> : null}
            </div>
          </div>
        </PostCardContainer>
      </PostCardWrapper>
    );
  },
);

const PostCardWrapper = styled.div<{ $layout: PostCardLayout }>`
  &.post-card-wrapper {
    display: block;
    .post-card-article-link {
      color: ${({ theme }) => theme.colorPalette.black};
      &:hover {
        color: ${({ theme }) => theme.colorPalette.blue500};
      }
    }
    .post-card-hero,
    .post-card-hero-compact {
      background: ${({ theme }) => theme.colorPalette.neutral900};
      @media screen and (max-width: 1024px) {
        margin-bottom: 12px;
      }
      .post-card-text {
        background: ${({ theme }) => theme.colorPalette.neutral900};
        color: white;
        margin-top: -3em;
        z-index: 0;
        &:before {
          content: ' ';
          background: linear-gradient(transparent, ${({ theme }) => theme.colorPalette.neutral900});
          height: 3em;
          margin-top: -3em;
          position: relative;
        }
        .post-card-text-wrapper {
          padding: 0.5em 0.75em;
          min-height: 56px;
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }

    .post-card-description {
      font-size: 12px;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-line-clamp: 2;
      color: ${({ theme }) => theme.colorPalette.gray700};
      font-weight: ${({ theme }) => theme.fontWeight.normal};
      line-height: 1.25rem;
    }

    .news-item-sponsored-tag {
      display: block;
      font-size: 13px;
      color: ${({ theme }) => theme.colorPalette.blue500};
    }

    .image-wrapper {
      position: relative;
      overflow: hidden;
      border-radius: ${({ theme }) => theme.borderRadius.sm};
      display: inline-flex;
      flex: 1;
      width: 100%;
      height: 100%;
      img {
        object-fit: cover;
      }
    }

    .post-card-default {
      .image-wrapper {
        height: 360px;

        @media (max-width: 768px) {
          height: 108px;
        }
      }
    }

    .post-card-card {
      border-radius: ${({ theme }) => theme.borderRadius.md};
      background: ${({ theme }) => theme.colorPalette.white};
      border: 1px solid ${({ theme }) => theme.colors.border};

      .image-wrapper {
        height: 130px;
        margin-bottom: 0;
        display: block;
      }
      .post-card-text-wrapper {
        padding: 1rem;
      }
      .post-title {
        font-size: ${({ theme }) => theme.fontSize.lg};
        margin-bottom: 0.25rem;
        display: block;
      }
      .post-teaser {
        font-size: ${({ theme }) => theme.fontSize.lg};
        color: ${({ theme }) => theme.colorPalette.gray500};
        font-weight: ${({ theme }) => theme.fontWeight.light};
      }
    }

    .post-card-feed {
      .post-card-description {
        font-size: ${({ theme }) => theme.fontSize.md};
      }
      .image-wrapper {
        max-height: 126px;
        min-width: 160px;
        max-width: 160px;
        min-height: 126px;

        @media (max-width: 768px) {
          max-height: 108px;
          min-height: 108px;
        }
      }
    }

    .post-card-size-compact {
      .image-wrapper {
        max-height: 180px;
        @media (max-width: 768px) {
          max-height: 108px;
          min-height: 108px;
        }
      }
    }

    .post-card-size-nano {
      .image-wrapper {
        max-height: 108px;

        @media (max-width: 768px) {
          max-height: 120px;
        }
      }
    }

    .post-card-feed {
      .post-card-title {
        font-size: 20px;
        -webkit-line-clamp: 3;
      }
      .post-card-image {
        margin-right: 16px;
        margin-bottom: 0;
      }
    }
    @media screen and (max-width: 800px) {
      .post-card-hero {
        .post-card-title {
          font-size: ${({ theme }) => theme.fontSize.xl};
        }
      }
      .post-card-feed {
        display: inline-flex;
        flex-direction: row;
        .post-card-title {
          font-size: ${({ theme }) => theme.fontSize.xl};
        }
        .post-card-image {
          height: 100px;
          min-height: 100px;
          min-width: 100px;
          max-height: 100px;
          max-width: 100px;
          width: 100px;
        }
        .post-card-text {
          margin-left: 0;
        }
        .post-card-description {
          font-size: 12px;
          font-weight: normal;
          line-height: 18px;
        }
      }
    }
    @media screen and (max-width: 500px) {
      .post-card-description {
        display: none;
      }
    }
  }
`;

interface PostCardContainerProps {
  layout?: PostCardLayout;
  size?: PostCardSize;
  title?: string;
}

const titleSize = (title?: string, size?: PostCardSize, layout?: PostCardLayout): string => {
  return size === 'compact' ? 'base' : layout === 'hero' && Number(title?.length) < 60 ? '4xl' : '2xl';
};

const PostCardContainer = styled.div<PostCardContainerProps>`
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  display: ${({ layout }) => (layout && ['feed', 'hero'].includes(layout) ? 'flex' : 'block')};
  font-size: ${({ layout, theme }) => (layout === 'default' ? theme.fontSize.base : theme.fontSize.xl)};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  overflow: hidden;
  flex-direction: ${({ layout }) => (layout === 'feed' ? 'row' : 'column')};
  .post-card-title {
    font-size: ${({ layout, size, theme, title }) => theme.fontSize[titleSize(title, size, layout)]};
  }
`;

const PostTitle = styled.span`
  &.post-title {
    line-height: 1.4em;
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
    display: -webkit-box;

    @media screen and (max-width: 600px) {
      font-size: 0.95rem;
      line-height: 1.4rem;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      text-overflow: ellipsis;
      display: inherit;
    }
  }
`;

const LineWrapper = styled.div`
  &.line-wrapper {
    -webkit-box-orient: vertical;
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ThreeLines = styled(LineWrapper)`
  &.three-line {
    -webkit-line-clamp: 4;
  }
  .badges {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-right: 8px;
    position: relative;
    vertical-align: middle;
  }
`;

const DynamicLines = styled(LineWrapper)`
  -webkit-line-clamp: 6;
  font-size: 1.5rem;

  @media screen and (max-width: 600px) {
    font-size: 0.7rem;
    -webkit-line-clamp: 3;
  }
`;
